// M1 throwaway: FE-owned chat lifecycle for flow (Live) analytics.
//
// The per-conversation context (workspace schema snapshot, flow versions) is
// assembled here; the prompt and tools come from the backend. The shared session
// state machine is in useAnalyticsChatSession.

import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import {
  type AnalyticsChatResult,
  useAnalyticsChatSession,
} from "src/analytics/copilot/useAnalyticsChatSession";
import { useCurrentWorkspace, useFlow } from "src/api/queries";
import { buildLiveAnalyticsAgentContext } from "src/flow/analytics/m1/liveAnalyticsAgentContext";
import { fetchPreflightBlocks } from "src/flow/analytics/m1/workspaceSchemaSnapshot";
import { useEnvironment } from "src/hooks/useEnvironment";

// Surface expiry on the FE a day before the backend hard limit so the user gets
// a graceful "start a new chat" message instead of a failed invoke at the edge.
const SESSION_EXPIRY_THRESHOLD_MS = 6 * 24 * 60 * 60 * 1000;
const SESSION_EXPIRED_MESSAGE =
  "This chat has expired. Start a new conversation to continue.";

const SESSIONS_STORAGE_KEY = "liveAnalyticsM1SessionsV2";

// Virtual ids for the live-analytics copilot product, defined in the agents
// module (`COPILOT_VIRTUAL_AGENT_IDS`). No agents-table rows exist behind them:
// the runner recognises them on the copilot namespace and resolves the config
// from the analytics backend instead of an agent version. The version id is
// constant — which config a session ran on is tracked by its content etag.
const LIVE_ANALYTICS_VIRTUAL_AGENT_ID = "ffffffff-0000-4000-8000-000000000001";
const LIVE_ANALYTICS_VIRTUAL_VERSION_ID =
  "ffffffff-0000-4000-8000-000000000002";

export const useLiveAnalyticsChat = ({
  flowId,
  flowName,
}: {
  flowId: string;
  flowName: string;
}): AnalyticsChatResult => {
  // Same source the chart's UUID→name relabeling uses; archived versions are
  // included because recent decisions can reference them.
  const flowQuery = useFlow(flowId, { includeArchivedFlowVersions: true });
  // Names are truncated to bound prompt size.
  const flowVersionIdToName = useMemo(
    () =>
      Object.fromEntries(
        (flowQuery.data?.versions ?? []).map(
          (v) => [v.id, v.name.slice(0, 40)] as const,
        ),
      ),
    [flowQuery.data?.versions],
  );
  // Version names still gate the snapshot, which carries them as data. Errors
  // release the gate — names just fall back to UUIDs.
  const flowVersionsResolved = flowQuery.isSuccess || flowQuery.isError;

  const { data: workspace } = useCurrentWorkspace();
  const demoMode = workspace?.config?.live_analytics_demo_mode;
  const [environment] = useEnvironment();

  // Nothing to provision: the virtual agent needs no agent or version record,
  // so this resolves without a round trip and the first message goes straight
  // to invoke.
  const provision = useCallback(
    () => Promise.resolve(LIVE_ANALYTICS_VIRTUAL_AGENT_ID),
    [],
  );

  // The schema snapshot and version list travel as structured_input on the first
  // message of a conversation, so they're scoped to the flow rather than to an
  // agent: one fetch per flow, with the query owning the caching and the retries.
  const baseUrl = workspace?.base_url;
  const contextQuery = useQuery({
    queryKey: ["liveAnalyticsAgentContext", baseUrl, flowId],
    queryFn: () =>
      fetchPreflightBlocks(baseUrl as string, flowId, flowVersionIdToName),
    enabled: Boolean(baseUrl) && flowVersionsResolved,
    staleTime: Infinity,
    retry: 1,
    // the global default sends a 500 to the page's error boundary; this query degrades instead
    throwOnError: false,
  });

  // On failure the flow context still goes, without the snapshot: an empty
  // `flow_versions` would assert the flow has had no versions in 30 days, and a
  // zeroed schema that the flow has no keys. Omitted, the agent discovers keys
  // on demand — which is what the prompt tells it to do.
  const initialStructuredInput = contextQuery.isError
    ? { flow_id: flowId, flow_name: flowName }
    : contextQuery.data
      ? buildLiveAnalyticsAgentContext({
          flowId,
          flowName,
          workspaceSchema: contextQuery.data.workspaceSchema,
          flowVersions: contextQuery.data.flowVersions,
        })
      : null;

  return useAnalyticsChatSession({
    storageKey: SESSIONS_STORAGE_KEY,
    sessionSlotKey: flowId,
    environment,
    provision,
    agentVersionId: LIVE_ANALYTICS_VIRTUAL_VERSION_ID,
    provisionReady: flowVersionsResolved,
    // `null` until the snapshot resolves; the hook buffers sends until then.
    initialStructuredInput,
    sessionExpiryThresholdMs: SESSION_EXPIRY_THRESHOLD_MS,
    sessionExpiredMessage: SESSION_EXPIRED_MESSAGE,
    // The demo prefix anchors "today" to the dataset's newest row instead.
    injectCurrentDate: !demoMode,
  });
};
